import { useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTheme } from "../context/ThemeProvider";
import { blurHashToDataUrl } from "../utils/blurHashPaint";
import {
  aspectPaddingBottomPct,
  fitMediaInside,
} from "../utils/mediaBubbleBox";
import TgMediaProgressRing from "./TgMediaProgressRing";
import TgMediaRetryButton from "./TgMediaRetryButton";

interface LoadingSkeletonImageDynamicProps {
  image_url: string;
  /** Tiny LQIP — only used if no blur_hash */
  preview_url?: string;
  /** BlurHash — instant paint, 0 network */
  blur_hash?: string | null;
  /** "1152x896" from DB — reserves bubble before decode */
  dims?: string;
  image_height: string;
  image_width: string;
  radius: string;
  className?: string;
  onClickFunc?: () => void;
  naturalSize?: boolean;
  naturalSizeCompact?: boolean;
  showProgressRing?: boolean;
}

function parseDims(raw?: string): { w: number; h: number } | null {
  if (!raw) return null;
  const m = String(raw).trim().match(/^(\d+)\s*[x×]\s*(\d+)$/i);
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  if (!w || !h) return null;
  return { w, h };
}

function pickBlurHoldMs() {
  return 40 + Math.floor(Math.random() * (1000 - 40 + 1));
}

/**
 * Chat media: dims-first immutable slot (like Telegram iOS).
 * Height reserved via padding-bottom % from WxH — never waits for decode.
 */
export default function LoadingSkeletonImageDynamic({
  image_url,
  preview_url,
  blur_hash,
  dims,
  image_height,
  image_width,
  radius,
  className,
  onClickFunc,
  naturalSize = false,
  naturalSizeCompact = false,
  showProgressRing,
}: LoadingSkeletonImageDynamicProps) {
  const [decoded, setDecoded] = useState(false);
  const [holdDone, setHoldDone] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [keepPlaceholder, setKeepPlaceholder] = useState(true);
  const [keepRing, setKeepRing] = useState(true);
  const [cancelled, setCancelled] = useState(false);
  const [loadKey, setLoadKey] = useState(0);
  const [cacheBust, setCacheBust] = useState(0);
  const [error, setError] = useState(false);
  /** Only when DB dims missing — never overrides reliable WxH */
  const [stubDims, setStubDims] = useState<{ w: number; h: number } | null>(
    null,
  );
  const holdMsRef = useRef(pickBlurHoldMs());
  const cancelledRef = useRef(false);
  const { theme } = useTheme();

  const blurUrl = useMemo(
    () => (blur_hash ? blurHashToDataUrl(blur_hash, 32, 32) : null),
    [blur_hash],
  );

  const lqipUrl =
    !blurUrl && preview_url && preview_url !== image_url ? preview_url : "";

  const ringEnabled = showProgressRing ?? naturalSize;

  const parsedDims = useMemo(() => parseDims(dims), [dims]);
  const hasReliableDims =
    !!parsedDims && parsedDims.w >= 64 && parsedDims.h >= 64;

  const maxBox = naturalSizeCompact
    ? { maxW: 288, maxH: 192 }
    : { maxW: 384, maxH: 448 };

  const srcDims = hasReliableDims
    ? parsedDims
    : stubDims || parsedDims;

  const box = useMemo(() => {
    if (!naturalSize) return null;
    if (srcDims) {
      return fitMediaInside(srcDims.w, srcDims.h, maxBox.maxW, maxBox.maxH);
    }
    return {
      width: Math.min(240, maxBox.maxW),
      height: Math.min(180, maxBox.maxH),
    };
  }, [naturalSize, srcDims, maxBox.maxW, maxBox.maxH]);

  const padPct = useMemo(() => {
    if (srcDims) return aspectPaddingBottomPct(srcDims.w, srcDims.h);
    if (box) return aspectPaddingBottomPct(box.width, box.height);
    return 75;
  }, [srcDims, box]);

  useEffect(() => {
    cancelledRef.current = false;
    holdMsRef.current = pickBlurHoldMs();
    setCancelled(false);
    setDecoded(false);
    setHoldDone(false);
    setRevealed(false);
    setKeepPlaceholder(true);
    setKeepRing(true);
    setError(false);
    setCacheBust(0);
    setStubDims(null);

    const t = window.setTimeout(() => {
      if (!cancelledRef.current) setHoldDone(true);
    }, holdMsRef.current);
    return () => window.clearTimeout(t);
  }, [image_url, preview_url, dims, blur_hash, loadKey]);

  useEffect(() => {
    if (cancelled) return;
    if (decoded && holdDone) setRevealed(true);
  }, [decoded, holdDone, cancelled]);

  useEffect(() => {
    if (!revealed || cancelled) return;
    const t = window.setTimeout(() => setKeepPlaceholder(false), 320);
    const t2 = window.setTimeout(() => setKeepRing(false), 420);
    return () => {
      window.clearTimeout(t);
      window.clearTimeout(t2);
    };
  }, [revealed, cancelled]);

  const softBg = theme === "dark" ? "bg-[#2a2a2a]" : "bg-[#ececec]";
  const showSoftPlaceholder =
    naturalSize && !blurUrl && !lqipUrl && !revealed && !error;

  const onFullLoad = (el: HTMLImageElement) => {
    if (cancelledRef.current) return;
    setDecoded(true);
    if (hasReliableDims || !naturalSize) return;
    const nw = el.naturalWidth;
    const nh = el.naturalHeight;
    if (!(nw > 0) || !(nh > 0)) return;
    setStubDims((prev) =>
      prev && prev.w === nw && prev.h === nh ? prev : { w: nw, h: nh },
    );
  };

  const handleCancel = () => {
    cancelledRef.current = true;
    setCancelled(true);
    setDecoded(false);
    setHoldDone(false);
    setRevealed(false);
    setKeepRing(false);
    setKeepPlaceholder(true);
  };

  const handleRetry = () => {
    cancelledRef.current = false;
    setCacheBust(Date.now());
    setLoadKey((k) => k + 1);
  };

  const fullImageSrc = useMemo(() => {
    if (!image_url) return "";
    if (!cacheBust) return image_url;
    const sep = image_url.includes("?") ? "&" : "?";
    return `${image_url}${sep}_r=${cacheBust}`;
  }, [image_url, cacheBust]);

  const markDecodeOk = (el: HTMLImageElement) => {
    if (cancelledRef.current) return;
    if (!(el.naturalWidth > 0) || !(el.naturalHeight > 0)) {
      setError(true);
      return;
    }
    onFullLoad(el);
  };

  const radiusStyle = radius || undefined;
  const showPlaceholder =
    (keepPlaceholder || cancelled) && Boolean(blurUrl || lqipUrl);
  const allowCancel = ringEnabled && naturalSize;

  if (!naturalSize) {
    const style: React.CSSProperties = {};
    if (image_height) style.height = image_height;
    if (image_width) style.width = image_width;
    if (radius) style.borderRadius = radius;
    const avatarPlaceholder =
      (keepPlaceholder || cancelled) && Boolean(blurUrl || lqipUrl);
    return (
      <div
        className={`relative overflow-hidden ${className ?? ""} ${!revealed && !blurUrl && !lqipUrl ? softBg : ""}`}
        style={style}
        onClick={onClickFunc}
      >
        {avatarPlaceholder && blurUrl && (
          <img
            src={blurUrl}
            alt=""
            draggable={false}
            aria-hidden
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover select-none"
            style={{
              opacity: revealed ? 0 : 1,
              filter: "blur(10px)",
              transform: "scale(1.08)",
              transition: "opacity 240ms ease",
              borderRadius: radiusStyle,
            }}
          />
        )}
        {avatarPlaceholder && !blurUrl && lqipUrl && (
          <img
            src={lqipUrl}
            alt=""
            draggable={false}
            aria-hidden
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover select-none"
            style={{
              opacity: revealed ? 0 : 1,
              filter: "blur(8px)",
              transform: "scale(1.05)",
              transition: "opacity 220ms ease",
              borderRadius: radiusStyle,
            }}
          />
        )}
        {image_url && (
          <img
            key={loadKey}
            src={fullImageSrc}
            alt=""
            draggable={false}
            decoding="async"
            className="relative h-full w-full object-cover select-none"
            style={{
              opacity: blurUrl || lqipUrl ? (revealed ? 1 : 0) : 1,
              transition: "opacity 280ms ease",
              borderRadius: radiusStyle,
            }}
            onLoad={(e) => markDecodeOk(e.currentTarget)}
            onError={() => {
              if (!cancelledRef.current) setError(true);
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={`relative inline-block max-w-full overflow-hidden ${!revealed && !blurUrl ? softBg : ""} ${className ?? ""}`}
      style={{
        width: box?.width,
        maxWidth: "100%",
        borderRadius: radiusStyle,
      }}
      onClick={(e) => {
        if (cancelled) {
          e.stopPropagation();
          handleRetry();
          return;
        }
        onClickFunc?.();
      }}
    >
      {/* Immutable height slot — % of width, same math as iOS f(w,h)+clamp */}
      <div
        aria-hidden
        className="pointer-events-none block w-full"
        style={{ paddingBottom: `${padPct}%`, height: 0 }}
      />

      {showSoftPlaceholder && (
        <Skeleton
          baseColor={theme === "dark" ? "#1d1d1d" : "#DFDFDF"}
          highlightColor={theme === "dark" ? "#252525" : "#fff"}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            borderRadius: radiusStyle,
          }}
        />
      )}

      {showPlaceholder && blurUrl && (
        <img
          src={blurUrl}
          alt=""
          draggable={false}
          aria-hidden
          decoding="async"
          width={srcDims?.w ?? box?.width}
          height={srcDims?.h ?? box?.height}
          className="absolute inset-0 h-full w-full object-cover select-none"
          style={{
            opacity: revealed && !cancelled ? 0 : 1,
            filter: "blur(14px)",
            transform: "scale(1.08)",
            transition: "opacity 280ms ease",
            borderRadius: radiusStyle,
          }}
        />
      )}

      {showPlaceholder && !blurUrl && lqipUrl && (
        <img
          src={lqipUrl}
          alt=""
          draggable={false}
          aria-hidden
          decoding="async"
          width={srcDims?.w ?? box?.width}
          height={srcDims?.h ?? box?.height}
          className="absolute inset-0 h-full w-full object-cover select-none"
          style={{
            opacity: revealed && !cancelled ? 0 : 1,
            filter: "blur(10px)",
            transform: "scale(1.05)",
            transition: "opacity 240ms ease",
            borderRadius: radiusStyle,
          }}
        />
      )}

      {image_url && !cancelled && (
        <img
          key={loadKey}
          src={fullImageSrc}
          alt=""
          draggable={false}
          decoding="async"
          width={srcDims?.w ?? box?.width}
          height={srcDims?.h ?? box?.height}
          className="absolute inset-0 h-full w-full object-cover select-none"
          style={{
            opacity: revealed ? 1 : 0,
            transition: "opacity 320ms ease",
            borderRadius: radiusStyle,
          }}
          onLoad={(e) => markDecodeOk(e.currentTarget)}
          onError={() => {
            if (!cancelledRef.current) setError(true);
          }}
        />
      )}

      {ringEnabled && keepRing && !error && !cancelled && (
        <TgMediaProgressRing
          active={!revealed}
          done={revealed}
          size={naturalSizeCompact ? 36 : 44}
          onCancel={allowCancel && !revealed ? handleCancel : undefined}
        />
      )}

      {(cancelled || error) && ringEnabled && (
        <TgMediaRetryButton
          size={naturalSizeCompact ? 36 : 44}
          onRetry={handleRetry}
        />
      )}
    </div>
  );
}
